import * as XLSX from 'xlsx';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { t } from '../../i18next';
import { useAlertBar } from './AlertBarContext';

let svcWorker = null;

function isServiceWorkerAvailable() {
  return 'serviceWorker' in navigator;
}

export async function initServiceWorker() {
  try {
    if (!isServiceWorkerAvailable()) {
      console.error('Service worker interface is not available.');
      return;
    }

    const swRegistration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      type: 'module',
      updateViaCache: 'none',
    });

    svcWorker = swRegistration.installing || swRegistration.waiting || swRegistration.active;

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      svcWorker = navigator.serviceWorker.controller;
    });
  } catch (error) {
    console.error(`Service worker registration failed with ${error.message}`);
  }
}

function sendSWMessage(msg) {
  try {
    if (svcWorker) {
      svcWorker.postMessage(msg);
    } else {
      navigator.serviceWorker.controller.postMessage(msg);
    }
  } catch (error) {
    console.error(`Send message to service worker failed with ${error.message}`);
    throw error;
  }
}

function processResult(fileName: string, arrayBuffer: ArrayBuffer) {
  const fileExt = fileName.split('.').pop();
  if (fileExt === 'xlsx') {
    const u8 = new Uint8Array(arrayBuffer);
    const wb = XLSX.read(u8);
    XLSX.writeFile(wb, fileName, { compression: true });
  } else {
    const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

const ServiceWorkerContext = createContext(null);

const ServiceWorkerStatus = {
  Starting: 'starting',
  Ready: 'ready',
  ReportInProgress: 'reportInProgress',
  Unavailable: 'serviceWorkerNotAvailable',
} as const;

type ServiceWorkerStatus =
  (typeof ServiceWorkerStatus)[keyof typeof ServiceWorkerStatus];

export default function ServiceWorkerContextProvider({ children }) {
  const [state, setState] = useState<ServiceWorkerStatus>(ServiceWorkerStatus.Starting);
  const { setAlertbar } = useAlertBar();

  useEffect(() => {
    if (!isServiceWorkerAvailable()) {
      setState(ServiceWorkerStatus.Unavailable);
      return;
    }
    navigator.serviceWorker.addEventListener('message', onSWMessage);
    setState(ServiceWorkerStatus.Ready);
  }, []);

  function onSWMessage(event) {
    const { data } = event;
    try {
      if (data.errors) {
        console.error('Fetch report failed with: ', data.errors);
        const msgFromServer: string = data.errors[0]?.message || 'Unknown error';
        const lowerMsg = msgFromServer.toLowerCase();
        if (lowerMsg.includes('selected date range')) {
          setAlertbar({
            children: t('export_report.no_records_date_range'), 
            severity: 'warning',
          })
        } else if (lowerMsg.includes('no records')) {
          setAlertbar({
            children: t('export_report.no_records'), 
            severity: 'warning',
          })
        } else{
          setAlertbar({
            children: t('export_report.error', { msg: msgFromServer }),
            severity: 'error',
          });
        }
        
        return;
      }

      const { fileName, arrayBuf } = data;
      processResult(fileName, arrayBuf);
    } catch (error) {
      setAlertbar({
        children: t('export_report.exception', { msg: error.message }),
        severity: 'error',
      });
    } finally {
      setState(ServiceWorkerStatus.Ready);
    }
  }

  const fetchReport = ({ fileName, url, options }) => {
    if (state === ServiceWorkerStatus.Unavailable) {
      setAlertbar({ children: t('export_report.cannot_request'), severity: 'error' });
      return;
    }

    if (state !== ServiceWorkerStatus.Ready) {
      console.error(`State is not ready. Fetching report not possible. Current state: ${state}`);
      return;
    }

    try {
      sendSWMessage({ fileName, url, options });
      setState(ServiceWorkerStatus.ReportInProgress);
    } catch {
      setAlertbar({ children: t('export_report.cannot_request'), severity: 'error' });
    }
  };

  const isReportInProgress = () => state === ServiceWorkerStatus.ReportInProgress;

  const value = useMemo(
    () => ({ fetchReport, isReportInProgress }),
    [fetchReport, isReportInProgress],
  );

  return <ServiceWorkerContext.Provider value={value}>{children}</ServiceWorkerContext.Provider>;
}

export function useSvcWorkerContext() {
  const context = useContext(ServiceWorkerContext);
  if (!context) {
    throw new Error('SvcWorker Context must be used within a Provider');
  }
  return context;
}
